/**
 * LocalStorage persistence — save/load scores and progression.
 *
 * All data is stored under the 'triggernometry' key as a single
 * JSON blob. Reads are cached in memory; writes are debounced.
 */

const STORAGE_KEY = 'triggernometry';

// ─── Types ─────────────────────────────────────────────────

export interface LevelRecord {
  levelId: string;
  bestScore: number;
  bestTimeMs: number;
  bestConvergence: number;
  bestEleganceRatio: number;
  starsCollected: [boolean, boolean, boolean];
  fragmentsCollected: boolean[];
  completionCount: number;
  attemptCount: number;
  practiceProgress: number;
  timestamp: number;
}

export interface GameSettings {
  masterVolume: number;
  musicVolume: number;
  sfxVolume: number;
  defaultAuthor?: string;
  tourDismissed?: boolean;
}

export interface EditorAutosave {
  levelId: string;
  data: import('@/game/level').LevelData;
  ts: number;
}

export interface Session {
  token: string;
  userId: string;
  displayName: string;
  discriminator: string;
}

export type PlayerShape =
  | 'equilateral'
  | 'right-triangle'
  | 'square'
  | 'pentagon'
  | 'circle';

export type ActiveReward =
  | { kind: 'color';          rewardId: string; color: string }
  | { kind: 'shape';          rewardId: string; shape: PlayerShape }
  | { kind: 'googly-eyes';    rewardId: string }
  | { kind: 'rainbow-trail';  rewardId: string };

export interface DailyRewardState {
  claimedAt: number;
  reward: ActiveReward;
  history: string[];
}

export interface SaveData {
  version: number;
  levels: Record<string, LevelRecord>;
  settings: GameSettings;
  unlockedLevels: string[];
  userLevels?: import('@/game/level').LevelData[];
  editorAutosave?: EditorAutosave | null;
  session?: Session;
  downloadedLevels?: import('@/game/level').LevelData[];
  downloadedLevelCache?: Record<string, {
    verifier: import('@/sync/sync-types').Verifier | null;
    currentVersionCompletionCount: number;
    cachedAt: number;
  }>;
  dailyReward?: DailyRewardState;
}

// ─── Defaults ──────────────────────────────────────────────

const DEFAULT_SETTINGS: GameSettings = {
  masterVolume: 0.7,
  musicVolume: 0.5,
  sfxVolume: 0.8,
};

function createDefaultSave(): SaveData {
  return {
    version: 1,
    levels: {},
    settings: { ...DEFAULT_SETTINGS },
    unlockedLevels: ['first-derivative'], // First level always unlocked; rest unlock via progression
  };
}

function createDefaultRecord(levelId: string): LevelRecord {
  return {
    levelId,
    bestScore: 0,
    bestTimeMs: 0,
    bestConvergence: 0,
    bestEleganceRatio: 0,
    starsCollected: [false, false, false],
    fragmentsCollected: [],
    completionCount: 0,
    attemptCount: 0,
    practiceProgress: 0,
    timestamp: 0,
  };
}

// ─── Storage Manager ───────────────────────────────────────

export class StorageManager {
  private data: SaveData;

  constructor() {
    this.data = this.load();
  }

  private load(): SaveData {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (!raw) return createDefaultSave();
      const parsed = JSON.parse(raw) as SaveData;
      // Ensure defaults for any missing fields
      if (!parsed.settings) parsed.settings = { ...DEFAULT_SETTINGS };
      if (!parsed.unlockedLevels) parsed.unlockedLevels = [];
      if (!Array.isArray(parsed.userLevels)) parsed.userLevels = [];
      if (parsed.editorAutosave === undefined) parsed.editorAutosave = null;
      if (!Array.isArray(parsed.downloadedLevels)) parsed.downloadedLevels = [];
      // Ensure first level is always unlocked
      if (!parsed.unlockedLevels.includes('first-derivative')) {
        parsed.unlockedLevels.push('first-derivative');
      }
      return parsed;
    } catch {
      return createDefaultSave();
    }
  }

  private save(): void {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(this.data));
    } catch {
      console.warn('Failed to save to LocalStorage');
    }
  }

  /** Get the record for a level (creates default if none exists) */
  getRecord(levelId: string): LevelRecord {
    if (!this.data.levels[levelId]) {
      this.data.levels[levelId] = createDefaultRecord(levelId);
    }
    return this.data.levels[levelId];
  }

  /** Update a level record after completing a run */
  updateRecord(
    levelId: string,
    score: number,
    timeMs: number,
    stars: [boolean, boolean, boolean],
    fragments: boolean[],
    convergence: number,
    eleganceRatio: number,
    completed: boolean,
    progress: number,
  ): void {
    const rec = this.getRecord(levelId);

    rec.attemptCount++;

    if (completed) {
      rec.completionCount++;
      if (score > rec.bestScore) {
        rec.bestScore = score;
        rec.timestamp = Date.now();
      }
      if (rec.bestTimeMs === 0 || timeMs < rec.bestTimeMs) {
        rec.bestTimeMs = timeMs;
      }
    }

    // Stars are persistent — once collected, always collected
    for (let i = 0; i < 3; i++) {
      if (stars[i]) rec.starsCollected[i] = true;
    }

    // Fragments are persistent too
    for (let i = 0; i < fragments.length; i++) {
      if (!rec.fragmentsCollected[i]) rec.fragmentsCollected[i] = false;
      if (fragments[i]) rec.fragmentsCollected[i] = true;
    }

    if (convergence > rec.bestConvergence) rec.bestConvergence = convergence;
    if (eleganceRatio > rec.bestEleganceRatio) rec.bestEleganceRatio = eleganceRatio;
    if (progress > rec.practiceProgress) rec.practiceProgress = progress;

    this.save();
  }

  /** Check if a level is unlocked */
  isUnlocked(levelId: string): boolean {
    return this.data.unlockedLevels.includes(levelId);
  }

  /** Unlock a level */
  unlock(levelId: string): void {
    if (!this.data.unlockedLevels.includes(levelId)) {
      this.data.unlockedLevels.push(levelId);
      this.save();
    }
  }

  /** Get total stars collected across all levels */
  getTotalStars(): number {
    let total = 0;
    for (const rec of Object.values(this.data.levels)) {
      total += rec.starsCollected.filter(Boolean).length;
    }
    return total;
  }

  /** Get/set game settings */
  get settings(): GameSettings {
    return this.data.settings;
  }

  updateSettings(settings: Partial<GameSettings>): void {
    Object.assign(this.data.settings, settings);
    this.save();
  }

  /** Reset all data */
  reset(): void {
    this.data = createDefaultSave();
    this.save();
  }

  // ─── User Levels ───────────────────────────────────────────

  /** Return a shallow copy of the user-levels array, newest-first. */
  getUserLevels(): import('@/game/level').LevelData[] {
    return (this.data.userLevels ?? []).slice();
  }

  getUserLevel(id: string): import('@/game/level').LevelData | undefined {
    return (this.data.userLevels ?? []).find((l) => l.meta.id === id);
  }

  /** Insert or replace by meta.id. Inserts at the front (newest-first). */
  upsertUserLevel(level: import('@/game/level').LevelData): void {
    if (!this.data.userLevels) this.data.userLevels = [];
    const existing = this.data.userLevels.findIndex((l) => l.meta.id === level.meta.id);
    if (existing >= 0) {
      this.data.userLevels[existing] = level;
    } else {
      this.data.userLevels.unshift(level);
    }
    this.save();
  }

  removeUserLevel(id: string): void {
    if (!this.data.userLevels) return;
    this.data.userLevels = this.data.userLevels.filter((l) => l.meta.id !== id);
    this.save();
  }

  // ─── Autosave ──────────────────────────────────────────────

  getAutosave(): EditorAutosave | null {
    return this.data.editorAutosave ?? null;
  }

  setAutosave(levelId: string, data: import('@/game/level').LevelData): void {
    this.data.editorAutosave = { levelId, data, ts: Date.now() };
    this.save();
  }

  clearAutosave(): void {
    this.data.editorAutosave = null;
    this.save();
  }

  // ─── Session ───────────────────────────────────────────────

  getSession(): Session | undefined {
    return this.data.session;
  }

  setSession(s: Session): void {
    this.data.session = { ...s };
    this.save();
  }

  clearSession(): void {
    this.data.session = undefined;
    this.save();
  }

  // ─── Daily Reward ──────────────────────────────────────────

  getDailyReward(): DailyRewardState | undefined {
    return this.data.dailyReward;
  }

  setDailyReward(state: DailyRewardState): void {
    this.data.dailyReward = {
      claimedAt: state.claimedAt,
      reward: { ...state.reward },
      history: state.history.slice(),
    };
    this.save();
  }

  clearDailyReward(): void {
    this.data.dailyReward = undefined;
    this.save();
  }

  // ─── Downloaded Levels ────────────────────────────────────

  getDownloadedLevels(): import('@/game/level').LevelData[] {
    return (this.data.downloadedLevels ?? []).slice();
  }

  upsertDownloadedLevel(level: import('@/game/level').LevelData): void {
    if (!this.data.downloadedLevels) this.data.downloadedLevels = [];
    const remoteId = level.meta.remoteId;
    if (!remoteId) throw new Error('Downloaded level requires meta.remoteId');
    const idx = this.data.downloadedLevels.findIndex((l) => l.meta.remoteId === remoteId);
    if (idx >= 0) this.data.downloadedLevels[idx] = level;
    else this.data.downloadedLevels.unshift(level);
    this.save();
  }

  removeDownloadedLevel(remoteId: string): void {
    if (!this.data.downloadedLevels) return;
    this.data.downloadedLevels = this.data.downloadedLevels.filter(
      (l) => l.meta.remoteId !== remoteId,
    );
    if (this.data.downloadedLevelCache) {
      delete this.data.downloadedLevelCache[remoteId];
    }
    this.save();
  }

  getDownloadedLevelCache(remoteId: string):
    { verifier: import('@/sync/sync-types').Verifier | null; currentVersionCompletionCount: number; cachedAt: number } | null {
    return this.data.downloadedLevelCache?.[remoteId] ?? null;
  }

  setDownloadedLevelCache(remoteId: string,
    cache: { verifier: import('@/sync/sync-types').Verifier | null; currentVersionCompletionCount: number; cachedAt: number }): void {
    if (!this.data.downloadedLevelCache) this.data.downloadedLevelCache = {};
    this.data.downloadedLevelCache[remoteId] = cache;
    this.save();
  }
}
